import React, { ReactNode, useEffect, useState } from 'react';
import { DocumentWizardHeaderTitle } from './document-wizard-header-title/DocumentWizardHeaderTitle';
import { ReactComponent as CloseIcon } from '../../../shared/icons/close-icon.svg';
import './document-wizard-header.scss';
import PendingReviewWarningModal from './modals/PendingReviewWarningModal';
import { useDocumentRole } from '../../../shared/hooks/useDocumentRole/useDocumentRole';
import { DocumentSourceEnum } from '../../document-wizard/document-editor/constants/EditorConstats';

interface DocumentHeaderProps {
  onClose?: () => void;
  /** In DOCX-native Compare mode: shows a "Back to editor" button left of the
   *  document name and calls onExitCompare when clicked. */
  isViewingCompare?: boolean;
  onExitCompare?: () => void;
  title: string;
  titleBadge: ReactNode;
  canEdit: boolean;
  isUploaded: boolean;
  isReviewing: boolean;
  hasInviteSendingPermission: boolean;
  shouldShowEditorModeSwitcherActive: boolean;
  hasUserEditPermission: boolean;
  onSaveTitle?: (value: string) => void;
  lastSaved?: ReactNode;
  actions: ReactNode;
  dealId: string;
  teamId: string;
  documentId: string;
  dealTeamsData: any;
  onSave: (() => Promise<void>) | undefined;
  canShare: boolean;
  shouldShowInternalSendForSigning: boolean | null | undefined;
  shouldShowSendForSigning: boolean | null | undefined;
  isApprovalMode?: boolean;
  isApproverWithEditPermissions?: boolean;
  hideLeftNav?: boolean;
  /** DOCX-native editor: drop the header's bottom divider so the header and the
   *  editor toolbar below it read as one continuous bar. */
  isDocxNative?: boolean;
  isPreparingToSign?: boolean;
  isSigning?: boolean;
  isExecuted?: boolean;
  approvalStamp?: ReactNode;
  isApprovalRequestApproved?: boolean;
  documentSource?: DocumentSourceEnum | null;
}

export const DocumentWizardHeader = ({
  onClose,
  isViewingCompare,
  onExitCompare,
  title,
  titleBadge,
  canEdit,
  onSaveTitle,
  isReviewing,
  hasInviteSendingPermission,
  lastSaved,
  actions,
  isUploaded,
  hasUserEditPermission,
  dealId,
  teamId,
  documentId,
  dealTeamsData,
  onSave,
  canShare,
  shouldShowInternalSendForSigning,
  shouldShowSendForSigning,
  isApprovalMode = false,
  isApproverWithEditPermissions = false,
  hideLeftNav = false,
  isDocxNative = false,
  isPreparingToSign = false,
  isSigning = false,
  isExecuted = false,
  approvalStamp,
  isApprovalRequestApproved = false,
  documentSource,
}: DocumentHeaderProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { isDocumentCreator, userRole, hasCurrentTeamPossession } = useDocumentRole(documentId);

  // "Before You Go… are you ready to send the document for review or signing?"
  // only makes sense while you actually HOLD the document. The condition was
  // status + role only, so a counterparty who had already sent it back — and
  // who can now re-open it read-only (AwaitingCounterpartyView) — was still
  // asked to send a document that is no longer theirs to send.
  const shouldWarnBeforeLeaving =
    isReviewing && hasInviteSendingPermission && !isDocumentCreator && !!hasCurrentTeamPossession;

  const handleCloseClick = () => {
    if (shouldWarnBeforeLeaving) {
      setModalOpen(true);
    } else {
      onClose && onClose();
    }
  };

  return (
    <header
      className={`document-wizard-header${isDocxNative ? ' is-docx-native' : ''}`}
      data-testid="document-header"
    >
      <nav className="document-wizard-header__actions">
        <div className="col-lg-8 p-0 document-wizard-header__document-title-container">
          {onClose && (
            <button
              type="button"
              onClick={handleCloseClick}
              className={`document-wizard-header__main-close ${hideLeftNav ? 'document-wizard-header__main-close--square-blue' : ''}`}
              data-testid="document-header__close-cta"
            >
              <CloseIcon className="document-wizard-header__main-close-icon" />
            </button>
          )}
          <div className="document-wizard-header__left-group">
            {/* Compare mode: an explicit exit sits to the LEFT of the document
                name (inline with it). Its own top-row Back button was removed,
                so this is the sole way back. */}
            {isViewingCompare && onExitCompare && (
              <button
                type="button"
                onClick={onExitCompare}
                className="document-wizard-header__back-to-editor"
                aria-label="Back to editor"
              >
                ← Back to editor
              </button>
            )}
            {/* The "View" dropdown was removed — its only two options were Edit
                and Compare. Compare now lives in the editor's File menu
                ("Compare versions…"), so the document name sits at the left. */}
            <DocumentWizardHeaderTitle
              canEdit={canEdit}
              title={title}
              titleBadge={titleBadge}
              onSaveTitle={onSaveTitle}
            />
          </div>
        </div>
        <div className="col-lg-4 p-0 document-wizard-header__document-action-container">
          {approvalStamp && <div className="document-wizard-header__approval-stamp">{approvalStamp}</div>}
          {lastSaved && <div className="document-wizard-header__last-updated small">Last Saved:&nbsp;{lastSaved}</div>}
          {/* pr-2 (0.5rem) matches the editor toolbar's right inset so the Send
              control lines up with the mode dropdown instead of crowding the edge. */}
          <div className="pr-2 document-wizard-header__edit-actions">{actions}</div>
        </div>
      </nav>
      <PendingReviewWarningModal
        showModal={modalOpen}
        hideModal={() => setModalOpen(false)}
        onCloseDocument={onClose}
        canEdit={canEdit}
        documentId={documentId}
        teamId={teamId}
        dealId={dealId}
        dealTeamsData={dealTeamsData}
        onSave={onSave}
        canShare={canShare}
        shouldShowInternalSendForSigning={shouldShowInternalSendForSigning}
        shouldShowSendForSigning={shouldShowSendForSigning}
      />
    </header>
  );
};
